import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { createStatusTranslator, createTypeTranslator } from '../utils/statusTranslation';
import { formatDate } from "../utils/dateUtils";
import {
  Card,
  CardContent,
  IconButton,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchFiles, getFile } from "../redux/actions/fileActions";
import { CheckCircle } from "@mui/icons-material";
import { fetchTasks, getTasksByCaseId } from "../redux/actions/taskActions";
import NotesDialog from "./NotesDialog";
import { largeBold, mediumTitle, smallBold, thinBold, thinTitle, typoBase } from "../styles/typography";
import FinanceCard from "./FinanceCard";
import axios from "../utils/axiosConfig";
import Avatar from "@mui/material/Avatar";
import FileTaskCard from "./FileTaskCard";
import { fetchUsers } from "../redux/actions/authActions";
import AgendaFileCard from "./AgendaFileCard";
import { listAppointments } from "../redux/actions/appointmentActions";
import EventIcon from "@mui/icons-material/Event";
import GavelIcon from "@mui/icons-material/Gavel";
import NotesCard from "./NotesCard";
import PartiesCard from "./PartiesCard";
import DocumentsCard from "./DocumentsCard";
import TimesheetSummaryCard from "./TimesheetSummaryCard";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const SixDots = () => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(3, auto)", // 3 columns for two rows
      gap: "4px", // Gap between the dots
      justifyContent: "center", // Center the grid horizontally
      alignItems: "center", // Center the grid vertically
    }}
  >
    {[...Array(6)].map((_, index) => (
      <Box
        key={index}
        sx={{
          width: "3px", // Adjust the size of the dots
          height: "3px",
          backgroundColor: "grey",
          borderRadius: "50%",
        }}
      />
    ))}
  </Box>
);

const CardsGrid = () => {
  const { t } = useTranslation(['common', 'pages', 'forms']);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Create translators using shared utilities
  const translateStatus = createStatusTranslator(t);
  const translateType = createTypeTranslator(t);
  const { tasks = [] } = useSelector((state) => state.taskList);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded);
  const { appointments } = useSelector((state) => state.appointmentList);
  const { users } = useSelector((state) => state.userList);
  const { file, loading } = useSelector((state) => state.fileDetails);

  const [selectedFile, setSelectedFile] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newNoteDate, setNewNoteDate] = useState("");
  const [showNewNoteField, setShowNewNoteField] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);



  // Drag and drop state
  const [cards, setCards] = useState([
    { id: "1", content: "caseDetails" },
    { id: "2", content: "parties" },
    { id: "3", content: "tasks" },
    { id: "4", content: "agenda" },
    { id: "5", content: "notes" },
    { id: "6", content: "timesheets" },
    { id: "7", content: "finance" },
    { id: "8", content: "documents" },
  ]);
  const [draggingCard, setDraggingCard] = useState(null);

  useEffect(() => {
    dispatch(getFile(id));
    dispatch(fetchFiles());
    dispatch(getTasksByCaseId(id));
    dispatch(listAppointments());
    dispatch(fetchUsers());
  }, [dispatch, id]);

  useEffect(() => {
    if (file?._id) {
      fetchNotes();
    }
  }, [file]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`/api/files/${file._id}`);
      setNotes(response.data.notes);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const userFiles = (files || []).filter((file) => {
    if (user?.role === "Manager") {
      return true;
    } else if (user?.role === "Responsible Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user?.relationship &&
          member.role === "Responsible Lawyer"
      );
    } else if (user?.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user?.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  const filteredTasks2 = (tasks || []).filter((task) => id === task.case?._id);
  const fileAppointments = (appointments || []).filter((appointment) => id === appointment.case?._id);
  
  const handleOpenNotes = () => {
    setSelectedFile(file);
  };

  const handleCloseNotes = () => {
    setSelectedFile(null);
    setNewNote("");
    setNewNoteDate("");
    setShowNewNoteField(false);
  };

  const handleAddNote = () => {
    setNewNoteDate(formatDate(new Date()));
    setShowNewNoteField(true);
  };

  const handleSaveAllNotes = async () => {
    try {
      const updatePromises = notes.map((note) =>
        axios.put(`/api/files/${file._id}/notes/${note._id}`, {
          content: note.content,
        })
      );

      if (newNote) {
        updatePromises.push(
          axios.post(`/api/files/${file._id}/notes`, {
            content: newNote,
            date: new Date(),
          })
        );
      }

      await Promise.all(updatePromises);
      await fetchNotes();
      setNewNote("");
      setNewNoteDate("");
      setShowNewNoteField(false);
    } catch (error) {
      console.error("Error saving notes:", error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`/api/files/${file._id}/notes/${noteId}`);
      await fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleOpen2 = () => setOpen2(true);
  const handleClose2 = () => setOpen2(false);

  const handleNavigate = (path) => {
    const currentFile = files.find((file) => file._id === id);
    const owner = currentFile ? currentFile.officeMembers[0].member : null;
    navigate(path, { state: { caseId: id, owner } });
    handleClose();
  };

  // Drag and drop handlers
  const onDragStart = (start) => {
    console.log("Drag started for index:", start.source.index);
    setDraggingCard(start.source.index);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return; // If dropped outside

    const updatedCards = Array.from(cards);
    const [movedCard] = updatedCards.splice(result.source.index, 1);
    updatedCards.splice(result.destination.index, 0, movedCard);

    setCards(updatedCards);
    setDraggingCard(null); // Reset dragging state after drag ends
  };



  // Function to render card content based on type
  const renderCardContent = (cardType) => {
    switch (cardType) {
      case "caseDetails":
        return (
          <Card sx={{
            height: "550px",
            overflowY: "auto",
            maxHeight: "550px",
            backgroundColor: "white",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
            border: "none",
            borderRadius: 0,
            position: "relative",
            "&:hover .six-dots": {
              display: "flex",
            },
            cursor: "default",
          }}>
            <Box
              className="six-dots"
              sx={{
                display: "none", // Initially hidden
                position: "absolute",
                top: "5px",
                right: "50%",
                cursor: "grab",
                zIndex: 12,
              }}
            >
              <SixDots />
            </Box>
            <CardContent>
            {/* Top section with title and pencil icon */}
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography
                  sx={{
                    color: "rgb(53, 53, 53)",
                    display: "block",
                    fontFamily:
                      '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "13px",
                    fontWeight: 600,
                    height: "18px",
                    lineHeight: "18px",
                  }}
                >
                  {t('pages:files.case_details')}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  onClick={() => navigate(`/files/edit/${id}`)}
                  style={{ cursor: "pointer", padding: "5px" }}
                >
                  <EditIcon
                    sx={{
                      fontSize: "24px",
                      color: "#2B80B6",
                    }}
                  />
                </IconButton>
              </Grid>
            </Grid>

            {/* Content previously in the first tab */}
            {file && (
              <Box className="list-items" sx={{ paddingTop: 4 }}>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid lightgrey",
                    padding: "7px 0",
                  }}
                >
                  <Box style={{ ...thinBold, color: "#404040" }}>
                    {t('forms:labels.jurisdiction')}
                  </Box>
                  <Box style={{ ...typoBase }}>{translateType(file.type)}</Box>
                </Box>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid lightgrey",
                    padding: "7px 0",
                  }}
                >
                  <Box style={{ ...thinBold, color: "#404040" }}>{t('forms:labels.file_no')}</Box>
                  <Box style={{ ...typoBase }}>{file.fileNo}</Box>
                </Box>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid lightgrey",
                    padding: "7px 0",
                  }}
                >
                  <Box style={{ ...thinBold, color: "#404040" }}>
                    {t('common:created_on')}
                  </Box>
                  <Box style={{ ...typoBase }}>
                    {formatDate(file.createdOn)}
                  </Box>
                </Box>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid lightgrey",
                    padding: "7px 0",
                  }}
                >
                  <Box style={{ ...thinBold, color: "#404040" }}>{t('forms:labels.file_name')}</Box>
                  <Box style={{ ...typoBase }}>{file.name}</Box>
                </Box>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid lightgrey",
                    padding: "7px 0",
                  }}
                >
                  <Box style={{ ...thinBold, color: "#404040" }}>
                    {t('common:responsible_lawyer')}
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Box style={{ ...smallBold }}>
                      {file?.officeMembers?.[0]?.member?.firstName &&
                      file?.officeMembers?.[0]?.member?.lastName
                        ? `${file?.officeMembers[0].member.firstName} ${file?.officeMembers[0].member.lastName}`
                        : t('common:no_member_available')}
                    </Box>
                    <Avatar
                      alt={fileResponsibleLawyer?.firstName}
                      src={
                        fileResponsibleLawyer?.avatar
                          ? fileResponsibleLawyer?.avatar
                          : "/static/images/avatar/1.jpg"
                      }
                      style={{
                        width: "20px",
                        height: "20px",
                        marginLeft: "8px",
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid lightgrey",
                    padding: "7px 0",
                  }}
                >
                  <Box style={{ ...thinBold, color: "#404040" }}>
                    {t('common:file_status')}
                  </Box>
                  <Box style={{ ...typoBase }}>{translateStatus(file.status)}</Box>
                </Box>
                {file.status === 'Inactive' && file.inactiveDate && (
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderBottom: "1px solid lightgrey",
                      padding: "7px 0",
                    }}
                  >
                    <Box style={{ ...thinBold, color: "#404040" }}>
                      Inactive since
                    </Box>
                    <Box style={{ ...typoBase }}>
                      {formatDate(file.inactiveDate)}
                    </Box>
                  </Box>
                )}
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid lightgrey",
                    padding: "7px 0",
                  }}
                >
                  <Box style={{ ...thinBold, color: "#404040" }}>
                    {t('forms:labels.hourlyRate')}
                  </Box>
                  <Box style={{ ...typoBase }}>
                    {file.rateConfig?.hourlyRate ? `€${file.rateConfig.hourlyRate}` : t('common:fallbacks.notSet')}
                  </Box>
                </Box>
                
                {/* Client Details Section */}
                {file?.parties?.filter(party => party.type === 'Client').map((clientParty, index) => (
                  <React.Fragment key={clientParty._id || index}>
                    {/* Email client */}
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderBottom: "1px solid lightgrey",
                        padding: "7px 0",
                      }}
                    >
                      <Box style={{ ...thinBold, color: "#404040" }}>
                        {file?.parties?.filter(party => party.type === 'Client').length > 1 
                          ? `E-mail ${t('common:client')} ${index + 1}` 
                          : 'E-mail client'}
                      </Box>
                      <Box style={{ ...typoBase }}>
                        {clientParty.name?.email || 
                         clientParty.name?.contacts?.find(c => c.email)?.email || 
                         '-'}
                      </Box>
                    </Box>
                    
                    {/* Phone number client */}
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderBottom: "1px solid lightgrey",
                        padding: "7px 0",
                      }}
                    >
                      <Box style={{ ...thinBold, color: "#404040" }}>
                        {file?.parties?.filter(party => party.type === 'Client').length > 1 
                          ? `Telefoonnummer ${t('common:client')} ${index + 1}` 
                          : 'Telefoonnummer client'}
                      </Box>
                      <Box style={{ ...typoBase }}>
                        {clientParty.name?.contacts?.find(c => c.phone)?.phone || '-'}
                      </Box>
                    </Box>
                    
                    {/* IBAN */}
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderBottom: "1px solid lightgrey",
                        padding: "7px 0",
                      }}
                    >
                      <Box style={{ ...thinBold, color: "#404040" }}>
                        {file?.parties?.filter(party => party.type === 'Client').length > 1 
                          ? `IBAN ${t('common:client')} ${index + 1}` 
                          : 'IBAN'}
                      </Box>
                      <Box style={{ ...typoBase }}>
                        {clientParty.name?.bankAccounts?.find(b => b.accountNumber)?.accountNumber || '-'}
                      </Box>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            )}
          </CardContent>
          </Card>
        );

      case "parties":
        return (
          <Card sx={{
            height: "550px",
            overflowY: "auto",
            maxHeight: "550px",
            backgroundColor: "white",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
            border: "none",
            borderRadius: 0,
            position: "relative",
            "&:hover .six-dots": {
              display: "flex",
            },
            cursor: "default",
          }}>
            <Box
              className="six-dots"
              sx={{
                display: "none", // Initially hidden
                position: "absolute",
                top: "5px",
                right: "50%",
                cursor: "grab",
                zIndex: 12,
              }}
            >
              <SixDots />
            </Box>
            <CardContent>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography sx={{
                    color: "rgb(53, 53, 53)",
                    display: "block",
                    fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "12px",
                    fontWeight: 600,
                    height: "18px",
                    lineHeight: "18px",
                  }}>
                    {t('common:parties')}
                  </Typography>
                </Grid>
              </Grid>
              <PartiesCard file={file} />
            </CardContent>
          </Card>
        );

      case "tasks":
        return (
          <Card sx={{
            height: "550px",
            overflowY: "auto",
            maxHeight: "550px",
            backgroundColor: "white",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
            border: "none",
            borderRadius: 0,
            position: "relative",
            "&:hover .six-dots": {
              display: "flex",
            },
            cursor: "default",
          }}>
            <Box
              className="six-dots"
              sx={{
                display: "none", // Initially hidden
                position: "absolute",
                top: "5px",
                right: "50%",
                cursor: "grab",
                zIndex: 12,
              }}
            >
              <SixDots />
            </Box>
            <CardContent>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography sx={{
                    color: "rgb(53, 53, 53)",
                    display: "block",
                    fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "12px",
                    fontWeight: 600,
                    height: "18px",
                    lineHeight: "18px",
                  }}>
                    {t('common:tasks')}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={handleCreateTask} style={{ cursor: "pointer", padding: "5px" }}>
                    <AddIcon sx={{ fontSize: "24px", color: "#2B80B6" }} />
                  </IconButton>
                </Grid>
              </Grid>
              <FileTaskCard filteredTasks={filteredTasks2} />
            </CardContent>
          </Card>
        );

      case "agenda":
        return (
          <Card sx={{
            height: "550px",
            overflowY: "auto",
            maxHeight: "550px",
            backgroundColor: "white",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
            border: "none",
            borderRadius: 0,
            position: "relative",
            "&:hover .six-dots": {
              display: "flex",
            },
            cursor: "default",
          }}>
            <Box
              className="six-dots"
              sx={{
                display: "none", // Initially hidden
                position: "absolute",
                top: "5px",
                right: "50%",
                cursor: "grab",
                zIndex: 12,
              }}
            >
              <SixDots />
            </Box>
            <CardContent>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography sx={{
                    color: "rgb(53, 53, 53)",
                    display: "block",
                    fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "12px",
                    fontWeight: 600,
                    height: "18px",
                    lineHeight: "18px",
                  }}>
                    {t('common:agenda')}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={handleOpen} style={{ cursor: "pointer", padding: "5px" }}>
                    <AddIcon sx={{ fontSize: "24px", color: "#2B80B6" }} />
                  </IconButton>
                </Grid>
              </Grid>
              <AgendaFileCard appointments={filteredAppointments} />
            </CardContent>
          </Card>
        );

      case "notes":
        return (
          <Card sx={{
            height: "550px",
            overflowY: "auto",
            maxHeight: "550px",
            backgroundColor: "white",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
            border: "none",
            borderRadius: 0,
            position: "relative",
            "&:hover .six-dots": {
              display: "flex",
            },
            cursor: "default",
          }}>
            <Box
              className="six-dots"
              sx={{
                display: "none", // Initially hidden
                position: "absolute",
                top: "5px",
                right: "50%",
                cursor: "grab",
                zIndex: 12,
              }}
            >
              <SixDots />
            </Box>
            <CardContent>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography sx={{
                    color: "rgb(53, 53, 53)",
                    display: "block",
                    fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "12px",
                    fontWeight: 600,
                    height: "18px",
                    lineHeight: "18px",
                  }}>
                    {t('common:notes')}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={() => handleOpenNotes()} style={{ cursor: "pointer", padding: "5px" }}>
                    <AddIcon sx={{ fontSize: "24px", color: "#2B80B6" }} />
                  </IconButton>
                </Grid>
              </Grid>
              <NotesCard
                notes={notes}
                handleOpenNotes={handleOpenNotes}
                file={file}
                fileResponsibleLawyer={fileResponsibleLawyer}
                onDeleteNote={handleDeleteNote}
              />
            </CardContent>
          </Card>
        );

      case "timesheets":
        return (
          <Card sx={{
            height: "550px",
            overflowY: "auto",
            maxHeight: "550px",
            backgroundColor: "white",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
            border: "none",
            borderRadius: 0,
            position: "relative",
            "&:hover .six-dots": {
              display: "flex",
            },
            cursor: "default",
          }}>
            <Box
              className="six-dots"
              sx={{
                display: "none", // Initially hidden
                position: "absolute",
                top: "5px",
                right: "50%",
                cursor: "grab",
                zIndex: 12,
              }}
            >
              <SixDots />
            </Box>
            <CardContent>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography sx={{
                    color: "rgb(53, 53, 53)",
                    display: "block",
                    fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "12px",
                    fontWeight: 600,
                    height: "18px",
                    lineHeight: "18px",
                  }}>
                    {t('common:timesheets')}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={handleCreateTimesheet} style={{ cursor: "pointer", padding: "5px" }}>
                    <AddIcon sx={{ fontSize: "24px", color: "#2B80B6" }} />
                  </IconButton>
                </Grid>
              </Grid>
              <TimesheetSummaryCard timesheets={file?.timesheets || []} />
            </CardContent>
          </Card>
        );

      case "finance":
        return (
          <Card sx={{
            height: "550px",
            overflowY: "auto",
            maxHeight: "550px",
            backgroundColor: "white",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
            border: "none",
            borderRadius: 0,
            position: "relative",
            "&:hover .six-dots": {
              display: "flex",
            },
            cursor: "default",
          }}>
            <Box
              className="six-dots"
              sx={{
                display: "none", // Initially hidden
                position: "absolute",
                top: "5px",
                right: "50%",
                cursor: "grab",
                zIndex: 12,
              }}
            >
              <SixDots />
            </Box>
            <CardContent>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography sx={{
                    color: "rgb(53, 53, 53)",
                    display: "block",
                    fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "12px",
                    fontWeight: 600,
                    height: "18px",
                    lineHeight: "18px",
                  }}>
                    {t('common:finance')}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={handleOpen2} style={{ cursor: "pointer", padding: "5px" }}>
                    <AddIcon sx={{ fontSize: "24px", color: "#2B80B6" }} />
                  </IconButton>
                </Grid>
              </Grid>
              <FinanceCard />
            </CardContent>
          </Card>
        );

      case "documents":
        return (
          <Card sx={{
            height: "550px",
            overflowY: "auto",
            maxHeight: "550px",
            backgroundColor: "white",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
            border: "none",
            borderRadius: 0,
            position: "relative",
            "&:hover .six-dots": {
              display: "flex",
            },
            cursor: "default",
          }}>
            <Box
              className="six-dots"
              sx={{
                display: "none", // Initially hidden
                position: "absolute",
                top: "5px",
                right: "50%",
                cursor: "grab",
                zIndex: 12,
              }}
            >
              <SixDots />
            </Box>
            <CardContent>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography sx={{
                    color: "rgb(53, 53, 53)",
                    display: "block",
                    fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "12px",
                    fontWeight: 600,
                    height: "18px",
                    lineHeight: "18px",
                  }}>
                    {t('common:documents')}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={handleCreateDocument} style={{ cursor: "pointer", padding: "5px" }}>
                    <AddIcon sx={{ fontSize: "24px", color: "#2B80B6" }} />
                  </IconButton>
                </Grid>
              </Grid>
              <DocumentsCard documents={file?.documents || []} />
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  if (!files || files.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "32px 24px" }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleCreateTask = () => {
    const currentFile = files.find((file) => file._id === id);
    const owner = currentFile ? currentFile.officeMembers[0].member : null;
    navigate("/task/create", { state: { caseId: id, owner } });
  };

  const handleCreateDocument = () => {
    const currentFile = files.find((file) => file._id === id);
    const owner = currentFile ? currentFile.officeMembers[0].member : null;
    navigate("/documents/upload", { state: { caseId: id, owner } });
  };

  const handleCreateTimesheet = () => {
    const currentFile = files.find((file) => file._id === id);
    const owner = currentFile ? currentFile.officeMembers[0].member : null;
    navigate("/timesheets/create", { state: { caseId: id, owner } });
  };

  const fileResponsibleLawyer = users.find(
    (user) => user.relationship === file?.officeMembers?.[0]?.member._id
  );

  const filteredAppointments = fileAppointments
    .filter((appointment) => appointment.owner?._id.toString() === user?.relationship.toString())
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    .slice(0, 100); // Increased from 30 to 100 for better user experience

  return (
    <Box>
      <div style={{ position: "relative" }}>
        {/* First Grid - Visible */}
        <DragDropContext onDragEnd={handleDragEnd} onDragStart={onDragStart}>
          <Droppable droppableId="cards-droppable" direction="horizontal">
            {(provided, snapshot) => (
              <Grid
                container
                spacing={0}
                sx={{ margin: 0, padding: 2, position: "relative", zIndex: 1 }}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {cards.map((card, index) => (
                  <Draggable key={card.id} draggableId={card.id} index={index}>
                    {(provided, snapshot) => (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        sx={{
                          paddingRight: 2,
                          paddingTop: 2
                        }}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          transition: "transform 0.2s ease",
                          opacity: snapshot.isDragging ? 0.8 : 1,
                        }}
                      >
                        {renderCardContent(card.content)}
                      </Grid>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
        </DragDropContext>

        {/* Second Grid - Invisible (behind the first grid) */}
        <div
          style={{
            position: "absolute",
            top: "1rem",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
          }}
        >
          <Grid container spacing={0} sx={{ margin: 0, padding: 2 }}>
            {cards.map((card, index) => (
              <Grid
                key={card.id}
                item
                xs={12}
                sm={4}
                sx={{
                  paddingRight: 2,
                  paddingTop: 2
                }}
              >
                <Card
                  sx={{ height: "550px" }}
                  style={{
                    backgroundColor: "#e3f2fd",
                    display: draggingCard === index ? "block" : "none",
                    border: "1px solid #2196f3",
                  }}
                >
                  {console.log("the condition is", draggingCard === index)}
                </Card>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>

      {/* Dialogs */}
      <NotesDialog
        selectedFile={selectedFile}
        file={file}
        notes={notes}
        newNote={newNote}
        newNoteDate={newNoteDate}
        showNewNoteField={showNewNoteField}
        snackbarOpen={snackbarOpen}
        snackbarMessage={snackbarMessage}
        handleCloseNotes={handleCloseNotes}
        handleAddNote={handleAddNote}
        handleSaveAllNotes={handleSaveAllNotes}
        handleDeleteNote={handleDeleteNote}
        setNewNote={setNewNote}
        setShowNewNoteField={setShowNewNoteField}
        setNotes={setNotes}
        setSnackbarOpen={setSnackbarOpen}
      />

      <Dialog
        open={open2}
        onClose={handleClose2}
        PaperProps={{
          sx: {
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle>{t('common:add_new')}</DialogTitle>
        <DialogContent sx={{ backgroundColor: "white", display: "flex", gap: 2 }}>
          <Card
            sx={{
              width: "50%",
              padding: "20px 50px",
              height: "80px",
              cursor: "pointer",
              borderRadius: 0,
              border: "1px solid lightgrey",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "none",
            }}
            onClick={() => handleNavigate("/timesheets/create")}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <AccessTimeIcon sx={{ fontSize: "36px", color: "#007ac3" }} />
              <Typography variant="h6">{t('common:timesheets')}</Typography>
            </CardContent>
          </Card>
          <Card
            sx={{
              width: "50%",
              padding: "20px 50px",
              height: "80px",
              cursor: "pointer",
              borderRadius: 0,
              border: "1px solid lightgrey",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "none",
            }}
            onClick={() => handleNavigate("/finance/create-invoice")}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <ReceiptIcon sx={{ fontSize: "36px", color: "#007ac3" }} />
              <Typography variant="h6">{t('pages:finance.invoice')}</Typography>
            </CardContent>
          </Card>
          <Card
            sx={{
              width: "50%",
              padding: "20px 50px",
              height: "80px",
              cursor: "pointer",
              borderRadius: 0,
              border: "1px solid lightgrey",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "none",
            }}
            onClick={() => handleNavigate("/finance/create-credit")}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <CreditCardIcon sx={{ fontSize: "36px", color: "#007ac3" }} />
              <Typography variant="h6">{t('pages:finance.credits')}</Typography>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle>{t('common:add_new')}</DialogTitle>
        <DialogContent sx={{ backgroundColor: "white", display: "flex", gap: 2 }}>
          <Card
            sx={{
              width: "50%",
              padding: "20px 50px",
              height: "80px",
              cursor: "pointer",
              borderRadius: 0,
              border: "1px solid lightgrey",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "none",
            }}
            onClick={() => handleNavigate("/appointment/create")}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <EventIcon sx={{ fontSize: "36px", color: "#007ac3" }} />
              <Typography variant="h6">{t('common:appointment')}</Typography>
            </CardContent>
          </Card>
          <Card
            sx={{
              width: "50%",
              padding: "20px 50px",
              height: "80px",
              cursor: "pointer",
              borderRadius: 0,
              border: "1px solid lightgrey",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "none",
            }}
            onClick={() => handleNavigate("/hearing/create")}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <GavelIcon sx={{ fontSize: "36px", color: "#007ac3" }} />
              <Typography variant="h6">{t('common:hearing')}</Typography>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CardsGrid; 